import React from 'react';
import { CheckCircle2, Circle, ShieldCheck } from 'lucide-react';

/**
 * ChainProgress — horizontal step indicator for an approval chain.
 * Reads `chain_state` shape:
 *   { steps: [{ step_index, label, approver_role, status, approver_name, decided_at, note }],
 *     current_step: int, completed: bool }
 *
 * Hidden when there's only one step (the legacy single-step default).
 */
const ChainProgress = ({ chainState, requestId }) => {
  if (!chainState || !Array.isArray(chainState.steps) || chainState.steps.length < 2) {
    return null;
  }
  return (
    <div data-testid={`chain-progress-${requestId}`} className="bg-white border border-slate-200 rounded-md p-3 mb-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-2">
        <ShieldCheck className="w-3 h-3 text-[#002FA7]" />
        Approval Chain
        {chainState.completed && <span className="ml-1 text-emerald-700 normal-case">· complete</span>}
      </div>
      <ol className="flex items-stretch gap-0 overflow-x-auto">
        {chainState.steps.map((s, i) => {
          const isCurrent = !chainState.completed && i === chainState.current_step && s.status === 'pending';
          const isApproved = s.status === 'approved';
          const isFuture = i > chainState.current_step && s.status === 'pending';
          const colorRing = isApproved ? 'bg-emerald-500 text-white' : isCurrent ? 'bg-[#002FA7] text-white' : 'bg-slate-200 text-slate-500';
          const colorText = isApproved ? 'text-emerald-700' : isCurrent ? 'text-[#002FA7] font-semibold' : isFuture ? 'text-slate-500' : 'text-slate-700';
          return (
            <li key={i} data-testid={`chain-step-${i}`} className="flex-1 min-w-[140px] flex items-start gap-2">
              <div className="flex flex-col items-center pt-0.5">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${colorRing}`}>
                  {isApproved ? <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2.5} /> : <Circle className="w-2.5 h-2.5" fill={isCurrent ? 'currentColor' : 'transparent'} />}
                </div>
                {i < chainState.steps.length - 1 && (
                  <div className={`w-px flex-1 mt-0.5 mb-0.5 ${isApproved ? 'bg-emerald-300' : 'bg-slate-200'}`} style={{ minHeight: 16 }} aria-hidden />
                )}
              </div>
              <div className="flex-1 min-w-0 pb-3">
                <div className={`text-xs ${colorText}`}>{s.label}</div>
                <div className="text-[10px] text-slate-400 uppercase tracking-widest">
                  {s.approver_role.replace('_', ' ')}
                </div>
                {s.approver_name && (
                  <div className="text-[10px] text-emerald-700 mt-0.5">
                    ✓ {s.approver_name}
                  </div>
                )}
                {isCurrent && (
                  <div className="text-[10px] text-[#002FA7] font-semibold mt-0.5">awaiting</div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default ChainProgress;
